// Google Ads Setup Verification Utility
// This helps verify that Google Ads tracking is properly configured

export const verifyGoogleAdsSetup = () => {
  const results = {
    isConfigured: false,
    issues: [],
    warnings: [],
    recommendations: []
  };

  // Check environment variables
  const conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
  const conversionLabel = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;
  const enabled = process.env.REACT_APP_GOOGLE_ADS_ENABLED;

  console.log('🔍 Verifying Google Ads Setup...');
  console.log('Environment Variables:', {
    conversionId,
    conversionLabel,
    enabled
  });

  // Check conversion ID
  if (!conversionId || conversionId === 'AW-XXXXXXXXX') {
    results.issues.push('❌ Google Ads Conversion ID not set or using placeholder');
  } else if (!conversionId.startsWith('AW-')) {
    results.issues.push('❌ Invalid Google Ads Conversion ID format (should start with AW-)');
  } else {
    console.log('✅ Conversion ID configured:', conversionId);
  }

  // Check conversion label
  if (!conversionLabel || conversionLabel === 'PLACEHOLDER_CONVERSION_LABEL' || conversionLabel === 'XXXXXXXXXX') {
    results.issues.push('❌ Google Ads Conversion Label not set or using placeholder');
    results.recommendations.push('📝 Create a conversion action in Google Ads to get the conversion label');
  } else {
    console.log('✅ Conversion Label configured:', conversionLabel);
  }

  // Check if enabled
  if (enabled === 'false') {
    results.warnings.push('⚠️ Google Ads tracking is disabled');
  } else {
    console.log('✅ Google Ads tracking is enabled');
  }

  // Check if gtag is available (only in browser)
  if (typeof window !== 'undefined') {
    if (window.gtag) {
      console.log('✅ Google gtag is available');
    } else {
      results.warnings.push('⚠️ Google gtag not yet loaded (this is normal during initial page load)');
    }

    // Check if dataLayer exists
    if (window.dataLayer) {
      console.log('✅ Google dataLayer is available');
    } else {
      results.warnings.push('⚠️ Google dataLayer not found');
    }
  }

  // Determine if setup is complete
  results.isConfigured = results.issues.length === 0;

  // Add setup recommendations
  if (!results.isConfigured) {
    results.recommendations.push(
      '🎯 Complete these steps:',
      '1. Go to Google Ads → Tools & Settings → Conversions',
      '2. Create a new conversion action for "Website"',
      '3. Choose "Form submission" as the conversion type',
      '4. Copy the conversion label and update your .env file',
      '5. Redeploy your website'
    );
  }

  return results;
};

// Console-friendly verification function
export const logGoogleAdsSetup = () => {
  const results = verifyGoogleAdsSetup();
  
  console.log('\n🎯 GOOGLE ADS SETUP VERIFICATION');
  console.log('================================');
  
  if (results.isConfigured) {
    console.log('✅ Google Ads tracking is properly configured!');
  } else {
    console.log('❌ Google Ads tracking needs configuration');
  }

  if (results.issues.length > 0) {
    console.log('\n🚨 ISSUES TO FIX:');
    results.issues.forEach(issue => console.log(issue));
  }

  if (results.warnings.length > 0) {
    console.log('\n⚠️ WARNINGS:');
    results.warnings.forEach(warning => console.log(warning));
  }

  if (results.recommendations.length > 0) {
    console.log('\n📝 RECOMMENDATIONS:');
    results.recommendations.forEach(rec => console.log(rec));
  }

  console.log('\n================================\n');
  
  return results;
};

// Test conversion tracking (for development)
export const testConversionTracking = (testData = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('⚠️ Cannot test conversion tracking - gtag not available');
    return false;
  }

  const conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
  const conversionLabel = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;

  if (!conversionId || !conversionLabel || conversionLabel === 'PLACEHOLDER_CONVERSION_LABEL') {
    console.warn('⚠️ Cannot test conversion tracking - configuration incomplete');
    return false;
  }

  try {
    // Send a test conversion
    window.gtag('event', 'conversion', {
      'send_to': `${conversionId}/${conversionLabel}`,
      'value': testData.value || 1.0,
      'currency': 'EUR',
      'transaction_id': `test_${Date.now()}`,
      'custom_parameters': {
        'test_mode': true,
        'service_type': testData.serviceType || 'test'
      }
    });

    console.log('✅ Test conversion sent successfully!');
    console.log('Check Google Ads dashboard in 3-24 hours to see if it appears.');
    return true;
  } catch (error) {
    console.error('❌ Test conversion failed:', error);
    return false;
  }
};

const verifyGoogleAdsSetupModule = { verifyGoogleAdsSetup, logGoogleAdsSetup, testConversionTracking };
export default verifyGoogleAdsSetupModule;
